import { openWaitlist } from "../waitlist/waitlist";
import { openFindInStore } from "../find-in-store/find-in-store";
import { parseInventory } from "../find-in-store/inventory";
import bindBloomreachEvents from "../../helpers/bloomreach";

const SECTION_ID = "product-card-quick-add";

const requests = new Map();

/**
 * Fetches a product's quick add markup, once per URL and section for the
 * page's lifetime.
 *
 * @param {string} url Product URL
 * @param {string} [sectionId] Any section whose markup holds a <quick-add>
 * @returns {Promise<string>}
 */
export function loadQuickAdd(url, sectionId = SECTION_ID) {
  const key = `${sectionId}:${url}`;

  if (requests.has(key)) return requests.get(key);

  const sectionUrl = new URL(url, window.location.origin);
  sectionUrl.searchParams.set("section_id", sectionId);

  const request = fetch(sectionUrl)
    .then((response) => {
      if (!response.ok) throw new Error(`Quick add failed: ${response.status}`);
      return response.text();
    })
    .then((markup) => {
      const quickAdd = new DOMParser()
        .parseFromString(markup, "text/html")
        .querySelector("quick-add");

      if (!quickAdd) throw new Error(`No quick add markup for ${url}`);
      return quickAdd.outerHTML;
    });

  // Let a failed request be retried rather than cached.
  request.catch(() => requests.delete(key));
  requests.set(key, request);

  return request;
}

// For page bundles, which would otherwise import a second copy with a
// separate cache.
window.loadQuickAdd = loadQuickAdd;

/**
 * Resolves the chosen option values to a variant and adds it to the cart,
 * then hands over from the quick add modal to the mini cart. An out-of-stock
 * variant a store has in stock turns the button to Find In-Store, which opens
 * that drawer over this one; failing that, with a waitlist it turns to Notify
 * me, which opens the waitlist modal. Values with neither can't be chosen.
 */
class QuickAdd extends HTMLElement {
  connectedCallback() {
    const data = window.scrapeSectionData(this);

    this.variants = data.variants || [];
    this.waitlistMessage = data.waitlist || "";
    this.promoNote = data.promo_note || "";
    this.productLowStock = data.low_stock || 0;
    // Empty where Find In-Store isn't offered.
    this.storeStock = parseInventory(this);
    this.form = this.querySelector("form");
    this.submit = this.querySelector("[data-quick-add-submit]");
    this.preorderNote = this.querySelector("[data-quick-add-preorder]");
    this.waitlistNote = this.querySelector("[data-quick-add-waitlist]");
    this.lowStockNote = this.querySelector("[data-quick-add-low-stock]");
    this.error = this.querySelector("[data-quick-add-error]");
    // Kept across reconnects, by which point the button may be showing a
    // "Select a …" prompt rather than the rendered add label.
    this.addLabel ??= this.submit.textContent.trim();

    this.addEventListener("change", this._update);
    this.form.addEventListener("submit", this._onSubmit);

    // Liquid's per-value availability only holds against the selected
    // variant's other options, so it's worked out afresh here.
    this._update();

    bindBloomreachEvents();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this._update);
    this.form.removeEventListener("submit", this._onSubmit);
  }

  get _chipGroups() {
    return [...this.querySelectorAll("fieldset[data-option-index]")];
  }

  _update = (event) => {
    this.error.textContent = "";

    this._updateAvailability(this._selectedValues());

    // Without a waitlist, a combination sold out online and in every store
    // can't stand: the value just chosen wins, and the other options' choices
    // are cleared.
    const chosen = this._findVariant(this._selectedValues());
    if (
      !this.waitlistMessage &&
      chosen &&
      !chosen.available &&
      !this._isInStore(chosen)
    ) {
      const changedGroup = event?.target.closest("fieldset");

      this._chipGroups.forEach((group) => {
        if (group === changedGroup) return;

        const checked = group.querySelector(":checked");
        if (checked) checked.checked = false;
      });
      this._updateAvailability(this._selectedValues());
    }

    // Re-read: the passes above may have cleared a choice.
    this.variant = this._findVariant(this._selectedValues());

    // As on the PDP, the button prompts for the first option still to choose.
    const pendingGroup = this._chipGroups.find(
      (group) => !group.querySelector(":checked"),
    );

    const preorder = this.variant?.available ? this.variant.preorder : "";
    // Find In-Store wins over the waitlist, as on the PDP.
    const findInStore = this._isInStore(this.variant);
    const waitlist = !findInStore && !!this.variant?.waitlist;

    let label = this.addLabel;
    if (pendingGroup) {
      label = pendingGroup.dataset.selectLabel;
    } else if (findInStore) {
      label = this.dataset.localeFindInStore;
    } else if (waitlist) {
      label = this.dataset.localeNotifyMe;
    } else if (this.variant && !this.variant.available) {
      label = this.dataset.localeSoldOut;
    } else if (preorder) {
      label = this.dataset.localePreOrder;
    }

    this.submit.disabled =
      !this.variant?.available && !waitlist && !findInStore;
    this.submit.textContent = label;
    this.submit.toggleAttribute("aria-haspopup", waitlist || findInStore);

    this._updatePreorder(preorder);
    this._updateWaitlist(waitlist);
    this._updateLowStock(
      pendingGroup ? this.productLowStock : this.variant?.low_stock || 0,
    );
    if (this.variant) this._updatePrice(this.variant);
  };

  /**
   * @param {object|null} variant
   * @returns {boolean} Sold out online, but in stock at a store.
   */
  _isInStore(variant) {
    return (
      !!variant &&
      !variant.available &&
      Object.keys(this.storeStock[variant.id] || {}).length > 0
    );
  }

  // As on the PDP: the product's total while a chip is unchosen, the
  // variant's otherwise.
  _updateLowStock(quantity) {
    if (!this.lowStockNote) return;

    this.lowStockNote.hidden = !quantity;
    this.lowStockNote.textContent = window.theme.translations(
      this.dataset,
      "localeLowStock",
      { quantity },
    );
  }

  _updateWaitlist(waitlist) {
    if (!this.waitlistNote) return;

    this.waitlistNote.hidden = !waitlist;
    this.waitlistNote.textContent = waitlist ? this.waitlistMessage : "";
  }

  _updatePreorder(preorder) {
    if (!this.preorderNote) return;

    this.preorderNote.hidden = !preorder;
    this.preorderNote.textContent = window.theme.translations(
      this.dataset,
      "localePreorderText",
      { text: preorder },
    );
  }

  /**
   * @returns {Array<string|null>} Value per option position; null where a
   *   chip is still to be chosen.
   */
  _selectedValues() {
    const selected = [];

    this.querySelectorAll("[data-option-index]").forEach((element) => {
      const index = Number(element.dataset.optionIndex);

      selected[index] =
        element.tagName === "FIELDSET"
          ? element.querySelector(":checked")?.value || null
          : element.value;
    });

    return selected;
  }

  /**
   * @param {Array<string|null>} selected See _selectedValues
   * @returns {object|null} The variant, or null while a choice is pending.
   */
  _findVariant(selected) {
    if (selected.includes(null)) return null;

    return (
      this.variants.find((variant) =>
        variant.options.every((value, index) => value === selected[index]),
      ) || null
    );
  }

  // A value is sold out unless some in-stock variant carries it alongside
  // whatever is chosen in the other options, and disabled if no variant does
  // at all. Without a waitlist, a value no variant in stock online or at a
  // store carries in any combination is disabled too — judged against all
  // variants rather than the other choices, which could strand a shopper
  // between two in-stock variants that share no value.
  _updateAvailability(selected) {
    this._chipGroups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);

      group.querySelectorAll("input").forEach((input) => {
        const candidates = this.variants.filter(
          (variant) =>
            variant.options[index] === input.value &&
            variant.options.every(
              (value, otherIndex) =>
                otherIndex === index ||
                selected[otherIndex] === null ||
                value === selected[otherIndex],
            ),
        );
        const neverInStock =
          !this.waitlistMessage &&
          !this.variants.some(
            (variant) =>
              (variant.available || this._isInStore(variant)) &&
              variant.options[index] === input.value,
          );
        input.disabled = candidates.length === 0 || neverInStock;
        input.toggleAttribute(
          "data-sold-out",
          !candidates.some((variant) => variant.available),
        );
        if (input.disabled) input.checked = false;
      });
    });
  }

  _updatePrice(variant) {
    const price = this.querySelector("[data-quick-add-price]");
    if (!price) return;

    price.setAttribute("price", variant.price / 100);
    price.setAttribute("compare-at-price", variant.compare_at_price / 100);
    price.setAttribute("currency", window.Shopify.currency.active);
    price.setAttribute("dynamic", "");
  }

  _onSubmit = (event) => {
    event.preventDefault();

    if (this._isInStore(this.variant)) {
      openFindInStore(
        this.submit,
        this.dataset.findInStoreUrl,
        this.variant.options,
      );
      return;
    }

    if (this.variant?.waitlist) {
      openWaitlist(this.submit, this.dataset.waitlistUrl, this.variant.id);
      return;
    }

    if (!this.variant?.available) return;

    this.submit.disabled = true;

    const { id, preorder, preorder_property: preorderProperty } = this.variant;

    window.addToCart(
      {
        id,
        quantity: 1,
        ...((preorder || this.promoNote) && {
          properties: {
            ...(preorder && { Preorder: preorderProperty }),
            ...(this.promoNote && { Note: this.promoNote }),
          },
        }),
      },
      {
        onAfterAdd: () => {
          // The mini cart opens once the modal is fully closed, so the two
          // don't contend for the dimmer, scroll lock and focus trap.
          document.addEventListener(
            "markup-modal:closed",
            () =>
              requestAnimationFrame(() =>
                document.dispatchEvent(new CustomEvent("mini-cart:open")),
              ),
            { once: true },
          );
          document.dispatchEvent(new CustomEvent("markup-modal:close"));
        },
        onError: (response) => {
          this.submit.disabled = false;
          this.error.textContent =
            response?.description ||
            response?.message ||
            window.localeStrings.product.add_to_cart_error;
        },
      },
    );
  };
}

if (!customElements.get("quick-add")) {
  customElements.define("quick-add", QuickAdd);
}
